import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import WKT from 'ol/format/WKT';
import OlPolygon from 'ol/geom/Polygon';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { ZoneOfInterestDto, ZoneOfInterestType } from 'src/app/bo/zoneOfInterest';
import { PolygonType } from 'src/app/bo/polygon/tirsPolygone';
import { TirContour } from 'src/app/bo/tirContour';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { ZoneOfInterestService } from 'src/app/services/zone-of-interest.service';
import { StaticUtils } from 'src/app/utils/staticUtils';
import { MAP_PROJECTION } from '../../site-map.constants';
import { SiteMapStore } from '../../site-map.store';
import { SiteMapDrawShape } from '../../site-map.types';

const DEFAULT_DETECTION_THRESHOLD_SECONDS = 30;

const POLYGON_TYPE_KEYS = [
  { id: PolygonType.AutomaticFromHoles, key: 'POLYGON.AUTOMATIC' },
  { id: PolygonType.DrillingContour, key: 'POLYGON.DRILLED' },
  { id: PolygonType.MuckPile, key: 'POLYGON.MUCKPILE' },
  { id: PolygonType.DangerZone, key: 'POLYGON.DANGER_ZONE' },
];

const ZONE_TYPE_KEYS = [
  { id: ZoneOfInterestType.Crusher, key: 'ZOI.CRUSHER' },
  { id: ZoneOfInterestType.Garage, key: 'ZOI.GARAGE' },
];

/** Dialogues des outils de création : choix de la forme, puis formulaire du polygone ou de la zone d'intérêt. */
@Component({
  selector: 'app-site-map-draw-dialog',
  standalone: true,
  templateUrl: './draw-dialog.component.html',
  styleUrls: ['./draw-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FormsModule, PopupComponent, ButtonComponent, InputComponent, SelectComponent],
})
export class SiteMapDrawDialogComponent {
  protected readonly store = inject(SiteMapStore);
  private readonly http = inject(HttpService);
  private readonly zoneService = inject(ZoneOfInterestService);
  private readonly fonctions = inject(FonctionsService);
  private readonly translate = inject(TranslateService);

  /** Les libellés des listes sont traduits à la construction des options : on les recalcule au changement de langue. */
  private readonly lang = toSignal(this.translate.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  protected readonly blastOptions = computed<SelectOption[]>(() =>
    this.store.blasts().map(blast => ({ id: blast.tirId, label: blast.nom })));
  protected readonly polygonTypeOptions = computed<SelectOption[]>(() => this.translated(POLYGON_TYPE_KEYS));
  protected readonly zoneTypeOptions = computed<SelectOption[]>(() => this.translated(ZONE_TYPE_KEYS));

  protected readonly blastId = signal<number | null>(null);
  protected readonly polygonType = signal<PolygonType>(PolygonType.MuckPile);
  protected readonly zoneName = signal('');
  protected readonly zoneType = signal<number>(ZoneOfInterestType.Crusher);
  protected readonly zoneThreshold = signal(DEFAULT_DETECTION_THRESHOLD_SECONDS);
  protected readonly saving = signal(false);

  protected readonly dialogTitleKey = computed(() => (this.store.activeTool() === 'zone' ? 'ZOI.ZONE' : 'POLYGON.POLYGON'));
  protected readonly showShapePicker = computed(() =>
    (this.store.activeTool() === 'polygon' || this.store.activeTool() === 'zone')
    && !this.store.drawShape()
    && !this.store.drawnGeometry());
  protected readonly showPolygonForm = computed(() => this.store.activeTool() === 'polygon' && !!this.store.drawnGeometry());
  protected readonly showZoneForm = computed(() => this.store.activeTool() === 'zone' && !!this.store.drawnGeometry());
  protected readonly canSavePolygon = computed(() => this.blastId() !== null && !this.saving());
  protected readonly canSaveZone = computed(() => {
    const threshold = this.zoneThreshold();
    return this.zoneName().trim().length > 0 && Number.isInteger(threshold) && threshold >= 0 && !this.saving();
  });

  constructor() {
    effect(() => {
      if (!this.store.drawnGeometry()) return;
      untracked(() => {
        this.blastId.set(this.store.toolBlastId());
        this.zoneName.set('');
        this.zoneType.set(ZoneOfInterestType.Crusher);
        this.zoneThreshold.set(DEFAULT_DETECTION_THRESHOLD_SECONDS);
        this.saving.set(false);
      });
    });
  }

  protected chooseShape(shape: SiteMapDrawShape): void {
    this.store.drawShape.set(shape);
  }

  protected cancel(): void {
    this.store.closeTool();
  }

  protected savePolygon(): void {
    const geometry = this.store.drawnGeometry();
    const blastId = this.blastId();
    if (!geometry || blastId === null || this.saving()) return;

    const contour = new TirContour();
    contour.format = MAP_PROJECTION;
    contour.coordonnees = StaticUtils.doubleCoordinateArrayToString(geometry.coordinates);
    contour.typePolygone = this.polygonType();

    const siteId = this.store.siteId();
    this.saving.set(true);
    this.http.post<TirContour, unknown>(`Blasts/${blastId}/BlastOutlines`, contour).subscribe({
      next: () => {
        this.fonctions.launch_toast(true);
        // Si l'on a changé de site pendant l'envoi, le tir enregistré n'appartient plus à la carte affichée.
        if (this.store.siteId() !== siteId) return;
        this.store.notifyPolygonCreated(blastId, contour.typePolygone);
        this.store.closeTool();
      },
      error: () => {
        this.fonctions.launch_toast(false);
        this.saving.set(false);
      },
    });
  }

  protected saveZone(): void {
    const geometry = this.store.drawnGeometry();
    const siteId = this.store.siteId();
    if (!geometry || siteId === null || !this.canSaveZone()) return;

    const circle = geometry.circle;
    const dto: ZoneOfInterestDto = {
      id: 0,
      name: this.zoneName().trim(),
      type: this.zoneType(),
      geometryType: circle ? 'circle' : 'polygon',
      centerX: circle?.center[0],
      centerY: circle?.center[1],
      radius: circle?.radius,
      wktString: circle ? undefined : new WKT().writeGeometry(new OlPolygon(geometry.coordinates)),
      detectionThresholdSeconds: this.zoneThreshold(),
      isActive: true,
    };

    this.saving.set(true);
    this.zoneService.create(siteId, dto).subscribe({
      next: () => {
        this.fonctions.launch_toast(true);
        if (this.store.siteId() !== siteId) return;
        this.store.reloadZonesOfInterest();
        this.store.closeTool();
      },
      error: () => {
        this.fonctions.launch_toast(false);
        this.saving.set(false);
      },
    });
  }

  private translated(entries: { id: number; key: string }[]): SelectOption[] {
    this.lang();
    return entries.map(entry => ({ id: entry.id, label: this.translate.instant(entry.key) }));
  }
}
